import { useCallback, useMemo, useState } from "react";
import List from "components/common/ListWidget";
import { useTranslation } from "react-i18next";
import TopMenu from "components/Reports/TopMenu";
import { utils, writeFile } from "xlsx";
import moment from "moment";
import useOrders from "graphql/useOrders";
import { getDateHeader } from "helpers/dataHelper";
import { useOrderItemUserSummary } from "../../graphql/useOrderItemSummary";
import { useBusinessContext } from "../../BusinessApp";
import useUsers from "../../graphql/useUsers";
import CardInfo from "./CardInfo";
import s from "./styles";
import DocumentPDF from "./DocumentPDF";
import { printDocumentPDF } from "helpers/utils";

function ReportsEmployees() {
  const { t } = useTranslation();

  const { storeId, currencyFormat } = useBusinessContext();
  const [date, setDate] = useState({
    value: moment(),
    periodType: "date",
    range: { start: moment().startOf("day"), end: moment().endOf("day") },
  });

  const { orderItemUserSummary, loading: loading4 } = useOrderItemUserSummary({
    storeId,
    start: date.range.start.toDate(),
    end: date.range.end.toDate(),
    limit: 10,
    order: -1,
  });
  const { orders, loading: loading2 } = useOrders(storeId, date?.range);
  const { users, loading: loading1 } = useUsers();

  const data = useMemo(() => {
    if (loading1 || loading4) {
      // data is loading, wait
      return [];
    }
    const result = {};
    const processedOrderIds = [];
    orderItemUserSummary.forEach((item) => {
      const key = item._id.userId;
      if (!result[key]) {
        const user = users.find((user) => user._id === key);
        if (!user?.name) {
          return;
        }
        result[key] = {
          userName: user?.name,
          // total vendido
          total: 0,
          // cantidad de ventas
          count: 0,
          // cantidad de items
          quantity: 0,
          // total descontado
          discountTotal: 0,
          // cuenta de descuentos
          discountCount: 0,
          returns: 0,
          returnsTotal: 0,
          tips: 0,
        };
      }
      // si es factura suma, si no resta
      const sign = item._id.orderType === "cre" ? -1 : 1;
      if (item._id.type === "item") {
        result[key].total = result[key].total + item.total * sign;
        result[key].quantity = result[key].quantity + item.quantity * sign;
        result[key].count = result[key].count + item.count * sign;
      } else if (
        item._id.type === "itemDiscount" ||
        item._id.type === "orderDiscount"
      ) {
        result[key].discountTotal =
          result[key].discountTotal + item.total * -sign;
        result[key].discountCount =
          result[key].discountCount + item.count * -sign;
      }

      orders.forEach((order) => {
        if (key === order.billingUserId) {
          result[key].tips =
            result[key].tips + parseFloat(order.extraAmount1 || 0);
        }
        if (
          order.billingUserId === key &&
          order.type === "cre" &&
          !processedOrderIds.includes(order._id)
        ) {
          if (!processedOrderIds.includes(order._id)) {
            processedOrderIds.push(order._id);
          }
          result[key].returns = result[key].returns + order.quantity * 1;
          result[key].returnsTotal =
            result[key].returnsTotal + order.total * sign;
        }
      });
    });
    return Object.keys(result)
      .map((key) => ({
        ...result[key],
        userId: key,
      }))
      .slice(0, 8);
  }, [loading1, loading4, orderItemUserSummary, orders, users]);

  const tableValues = data
    .map((item) => [
      `${item.userName}`,
      `${item.returns} (${currencyFormat(item.returnsTotal)})`,
      `${Math.abs(item.discountCount)} (${currencyFormat(item.discountTotal)})`,
      `${item.count} (${currencyFormat(item.total)})`,
      `${currencyFormat(item.tips)}`,
    ])
    .sort((a, b) => b[3] - a[3]);

  const workersWithMostReturns = useMemo(
    () =>
      data
        .filter((item) => item.returns !== 0)
        .sort((a, b) => b.returns - a.returns)
        .slice(0, 4)
        .map((item) => ({
          name: item.userName,
          value: `${item.returns} (${currencyFormat(item.returnsTotal)})`,
        })),
    [data]
  );

  const workersWithMostDiscounts = useMemo(
    () =>
      data
        .filter((item) => item.discountCount !== 0)
        .sort((a, b) => Math.abs(b.discountCount) - Math.abs(a.discountCount))
        .slice(0, 4)
        .map((item) => ({
          name: item.userName,
          value: `${Math.abs(item.discountCount)} (${currencyFormat(
            item.discountTotal
          )})`,
        })),
    [data]
  );

  data.map((i) => {
    return [
      i.userName,

      i.returns,
      i.returnsTotal,

      i.discountCount,
      i.discountTotal,

      i.count,
      i.total,
    ];
  });

  const exportFileExcel = useCallback(() => {
    const worksheetData = [
      [
        `${t("containers.ReportsEmployee.reportsEmployeeFor")} ${getDateHeader(
          date.range.start,
          date.range.end
        )}`,
      ],
      [],
      [t("containers.ReportsEmployee.summarySales")],
      [
        t("containers.ReportsEmployee.employee"),

        `${t("containers.ReportsEmployee.return")}  ${t(
          "containers.ReportsEmployee.amount"
        )}`,
        `${t("containers.ReportsEmployee.return")}  ${t(
          "containers.ReportsEmployee.total"
        )}`,

        `${t("containers.ReportsEmployee.discount")}  ${t(
          "containers.ReportsEmployee.amount"
        )}`,
        `${t("containers.ReportsEmployee.discount")}  ${t(
          "containers.ReportsEmployee.total"
        )}`,

        `${t("containers.ReportsEmployee.sales")}  ${t(
          "containers.ReportsEmployee.amount"
        )}`,
        `${t("containers.ReportsEmployee.sales")}  ${t(
          "containers.ReportsEmployee.total"
        )}`,
        t("containers.ReportsEmployee.tips"),
      ],
      ...data.map((i) => {
        return [
          i.userName,

          i.returns,
          { t: "n", v: i.returnsTotal, z: "€ #,##0.00" },

          -i.discountCount,
          { t: "n", v: i.discountTotal, z: "€ #,##0.00" },

          i.count,
          { t: "n", v: i.total, z: "€ #,##0.00" },
          i.tips,
          { t: "n", v: i.tips, z: "€ #,##0.00" },
        ];
      }),
      [],
      [t("containers.ReportsEmployee.workersMostReturns")],
      [
        t("containers.ReportsEmployee.employee"),
        `${t("containers.ReportsEmployee.return")}  ${t(
          "containers.ReportsEmployee.amount"
        )}`,
        `${t("containers.ReportsEmployee.return")}  ${t(
          "containers.ReportsEmployee.total"
        )}`,
      ],
      ...data
        .sort((a, b) => b.returns - a.returns)
        .map((i) => {
          return [
            i.userName,
            i.returns,
            { t: "n", v: i.returnsTotal, z: "€ #,##0.00" },
          ];
        }),
      [],
      [t("containers.ReportsEmployee.workersMostDiscounts")],
      [
        t("containers.ReportsEmployee.employee"),

        `${t("containers.ReportsEmployee.discount")}  ${t(
          "containers.ReportsEmployee.amount"
        )}`,
        `${t("containers.ReportsEmployee.discount")}  ${t(
          "containers.ReportsEmployee.total"
        )}`,
      ],
      ...data
        .sort((a, b) => Math.abs(b.discountCount) - Math.abs(a.discountCount))
        .map((i) => {
          return [
            i.userName,
            -i.discountCount,
            { t: "n", v: i.discountTotal, z: "€ #,##0.00" },
          ];
        }),
    ];

    const workbook = utils.book_new();
    const sheetName = t("containers.ReportsEmployee.reportsEmployee");
    const worksheet = utils.aoa_to_sheet(worksheetData);
    utils.book_append_sheet(workbook, worksheet, sheetName);

    writeFile(
      workbook,
      `${t("containers.ReportsEmployee.reportsEmployee")}.xlsx`
    );
  }, [
    t,
    date.range.start,
    date.range.end,
    tableValues,
    workersWithMostReturns,
    workersWithMostDiscounts,
  ]);

  const getImageGraph = useCallback(async () => {
    await printDocumentPDF(
      <DocumentPDF
        data={{
          dateHeader: getDateHeader(date.range.start, date.range.end)[0],
          tableValues,
          workersWithMostReturns,
          workersWithMostDiscounts,
        }}
      />,
      `${t("containers.ReportsEmployee.reportsEmployee")}.pdf`
    );
  }, [
    date.range.end,
    date.range.start,
    t,
    tableValues,
    workersWithMostDiscounts,
    workersWithMostReturns,
  ]);

  const loading = loading1 || loading4 || loading2;

  return (
    <s.PageContainer>
      <TopMenu
        loading={loading}
        value={date.value}
        onChange={(value, periodType, range) => {
          setDate({ value, periodType, range });
        }}
        exportFileExcel={exportFileExcel}
        getImageGraph={getImageGraph}
        disabled={
          workersWithMostReturns.length === 0 &&
          workersWithMostDiscounts.length === 0 &&
          tableValues.length === 0
        }
      />

      <s.Container>
        <List
          width="700px"
          items={tableValues}
          loading={loading}
          title={t("containers.ReportsEmployee.summarySales")}
          columns={[
            {
              title: t("containers.ReportsEmployee.employee"),
            },
            {
              title: t("containers.ReportsEmployee.return"),
            },
            {
              title: t("containers.ReportsEmployee.discount"),
            },
            {
              title: t("containers.ReportsEmployee.sales"),
            },
            {
              title: t("containers.ReportsEmployee.tips"),
            },
          ]}
          tableStyle={{ gridTemplateColumns: "20fr 21fr 21fr 15fr 15fr " }}
        />

        <s.ChartComponentsWrapper>
          <CardInfo
            loading={loading}
            title={t("containers.ReportsEmployee.workersMostReturns")}
            data={workersWithMostReturns}
          />
          <CardInfo
            loading={loading}
            title={t("containers.ReportsEmployee.workersMostDiscounts")}
            data={workersWithMostDiscounts}
          />
        </s.ChartComponentsWrapper>
        {/* </s.Container> */}
      </s.Container>
    </s.PageContainer>
  );
}

export default ReportsEmployees;
